import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ListChecks, Plus, Save, Trash2, Edit2 } from "lucide-react";

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", reward_coins: 10, type: "one_time", requirement: "collect", requirement_count: 1 });
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState("");

  useEffect(() => { API.get("/admin/tasks").then(r => setTasks(r.data.tasks)).catch(() => {}); }, []);

  const create = async () => {
    setLoading("create");
    try {
      await API.post("/admin/tasks", form);
      toast.success("Task created");
      const { data } = await API.get("/admin/tasks"); setTasks(data.tasks);
      setShowCreate(false); setForm({ title: "", description: "", reward_coins: 10, type: "one_time", requirement: "collect", requirement_count: 1 });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const save = async (taskId) => {
    setLoading(`save-${taskId}`);
    try {
      await API.put(`/admin/tasks/${taskId}`, form);
      toast.success("Saved");
      const { data } = await API.get("/admin/tasks"); setTasks(data.tasks); setEditing(null);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const del = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try { await API.delete(`/admin/tasks/${taskId}`); toast.success("Deleted"); setTasks(prev => prev.filter(t => t.task_id !== taskId)); } catch { toast.error("Failed"); }
  };

  const REQS = [
    { value: "daily_login", label: "Daily Login" },
    { value: "collect", label: "Collect Resources" },
    { value: "sell", label: "Sell Resources" },
    { value: "ptc_watch", label: "Watch PTC Ads" },
    { value: "buy_pack", label: "Buy Pack" },
    { value: "buy_coins", label: "Buy Coins" },
    { value: "referral", label: "Refer Friends" },
    { value: "deposit", label: "Make Deposit" },
    { value: "withdraw", label: "Withdraw" },
    { value: "daily_reward", label: "Claim Daily Reward" },
    { value: "offerwall", label: "Complete Offerwall" },
    { value: "connect_wallet", label: "Connect Wallet" },
  ];

  return (
    <AdminLayout title="Task Management">
      <div className="space-y-4" data-testid="admin-tasks">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#94A3B8]">{tasks.length} tasks configured</p>
          <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase bg-[#00F0FF] text-[#0B0E14] rounded-lg"><Plus className="w-3 h-3" /> New Task</button>
        </div>

        {showCreate && (
          <div className="bg-[#131722] rounded-xl border border-[#00F0FF]/30 p-4 space-y-3">
            <h3 className="text-sm font-bold text-[#F8FAFC]">Create Task</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label className="text-[#94A3B8] text-xs">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
              <div><Label className="text-[#94A3B8] text-xs">Reward (coins)</Label><Input type="number" step="0.01" value={form.reward_coins} onChange={e => setForm({...form, reward_coins: parseFloat(e.target.value)})} className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-8 text-sm mt-1 font-mono-game" /></div>
              <div><Label className="text-[#94A3B8] text-xs">Requirement Count</Label><Input type="number" value={form.requirement_count} onChange={e => setForm({...form, requirement_count: parseInt(e.target.value)})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
              <div><Label className="text-[#94A3B8] text-xs">Type</Label><select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-xs"><option value="one_time">One Time</option><option value="daily">Daily</option></select></div>
              <div><Label className="text-[#94A3B8] text-xs">Requirement</Label><select value={form.requirement} onChange={e => setForm({...form, requirement: e.target.value})} className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-xs">{REQS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
              <div className="sm:col-span-3"><Label className="text-[#94A3B8] text-xs">Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
            </div>
            <button onClick={create} disabled={loading === "create"} className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50">Create</button>
          </div>
        )}

        {tasks.map(t => (
          <div key={t.task_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4" data-testid={`admin-task-${t.task_id}`}>
            {editing === t.task_id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div><Label className="text-[#94A3B8] text-xs">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Reward</Label><Input type="number" step="0.01" value={form.reward_coins} onChange={e => setForm({...form, reward_coins: parseFloat(e.target.value)})} className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-8 text-sm mt-1 font-mono-game" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Req Count</Label><Input type="number" value={form.requirement_count} onChange={e => setForm({...form, requirement_count: parseInt(e.target.value)})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Type</Label><select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-xs"><option value="one_time">One Time</option><option value="daily">Daily</option></select></div>
                  <div><Label className="text-[#94A3B8] text-xs">Requirement</Label><select value={form.requirement} onChange={e => setForm({...form, requirement: e.target.value})} className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-xs">{REQS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
                  <div><Label className="text-[#94A3B8] text-xs">Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => save(t.task_id)} disabled={!!loading} className="px-3 py-1.5 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" />Save</button>
                  <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs text-[#94A3B8] border border-[#2A3241] rounded-lg">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2"><p className="text-sm font-bold text-[#F8FAFC]">{t.title}</p><span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF]">{t.type}</span></div>
                  <p className="text-xs text-[#94A3B8]">{t.description} | Req: {t.requirement} x{t.requirement_count} | <span className="text-[#FFD600] font-mono-game">+{t.reward_coins}c</span></p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(t.task_id); setForm({ title: t.title, description: t.description || "", reward_coins: t.reward_coins, type: t.type, requirement: t.requirement, requirement_count: t.requirement_count || 1 }); }} className="p-1.5 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => del(t.task_id)} className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
